import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  List,
  ListItem,
  ListIcon,
  Grid,
  GridItem,
  SimpleGrid,
  Alert,
  AlertIcon,
  useToast,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Brain, Rocket, ChevronRight, Download, Calendar, Mail } from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';

const MotionBox = motion(Box);

const Investors = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const toast = useToast();
  const { trackEvent } = useAnalytics();

  const handlePitchDeckRequest = () => {
    trackEvent('pitch_deck_requested', {
      section: 'investors',
    });
    toast({
      title: 'Request Received',
      description: "We'll send the pitch deck to your email shortly.",
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
  };

  const handleScheduleCall = () => {
    trackEvent('investor_call_requested', {
      section: 'investors',
    });
    window.open('https://calendly.com/gallifreyai/investor-call', '_blank');
  };

  return (
    <Box
      id='investors'
      py={{ base: 12, md: 20 }}
      bg='brand.dark'
      bgGradient='linear(to-b, rgba(11, 61, 145, 0.2), brand.dark)'
      bgImage="linear-gradient(to bottom, rgba(2, 8, 20, 0.95), rgba(2, 8, 20, 0.95)), url('https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&w=2000&q=80')"
      bgAttachment={{ base: 'scroll', md: 'fixed' }}
      bgSize='cover'
      bgPosition='center'
      position='relative'
      onMouseEnter={() => trackEvent('section_viewed', { section: 'investors' })}>
      <Container maxW='1200px' px={{ base: 4, md: 6 }}>
        <MotionBox
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}>
          <VStack spacing={{ base: 8, md: 12 }} align='stretch'>
            {/* Hero Section */}
            <VStack spacing={{ base: 4, md: 6 }} align='stretch' textAlign='center'>
              <Heading
                as='h2'
                size={{ base: 'lg', md: 'xl' }}
                color='brand.light'
                letterSpacing='wider'>
                Revolutionizing Developer Workflows Through AI Innovation
              </Heading>
              <Text color='gray.300' fontSize={{ base: 'md', md: 'lg' }} maxW='800px' mx='auto'>
                We're building AI-powered solutions to make development more inclusive and
                efficient, focusing on tools that adapt to different ways of thinking and working.
              </Text>
            </VStack>

            {/* Market Opportunity & Vision */}
            <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={{ base: 6, md: 8 }}>
              <GridItem>
                <Box
                  p={{ base: 6, md: 8 }}
                  borderRadius='xl'
                  bg='rgba(11, 61, 145, 0.3)'
                  border='1px solid'
                  borderColor='brand.accent'>
                  <VStack align='stretch' spacing={4}>
                    <Heading as='h3' size='lg' color='brand.light'>
                      Our Vision
                    </Heading>
                    <List spacing={3}>
                      <ListItem color='gray.300' display='flex' alignItems='center'>
                        <ListIcon as={ChevronRight} color='brand.accent' />
                        <Text>
                          <Text as='span' color='brand.accent' fontWeight='bold'>
                            Innovation Focus:{' '}
                          </Text>
                          Creating adaptive AI testing and code review tools
                        </Text>
                      </ListItem>
                      <ListItem color='gray.300' display='flex' alignItems='center'>
                        <ListIcon as={ChevronRight} color='brand.accent' />
                        <Text>
                          <Text as='span' color='brand.accent' fontWeight='bold'>
                            Inclusive Development:{' '}
                          </Text>
                          Building tools that work for different thinking styles
                        </Text>
                      </ListItem>
                      <ListItem color='gray.300' display='flex' alignItems='center'>
                        <ListIcon as={ChevronRight} color='brand.accent' />
                        <Text>
                          <Text as='span' color='brand.accent' fontWeight='bold'>
                            Future Growth:{' '}
                          </Text>
                          Expanding our suite of developer tools
                        </Text>
                      </ListItem>
                    </List>
                  </VStack>
                </Box>
              </GridItem>

              <GridItem>
                <Box
                  p={{ base: 6, md: 8 }}
                  borderRadius='xl'
                  bg='rgba(11, 61, 145, 0.3)'
                  border='1px solid'
                  borderColor='brand.accent'>
                  <VStack align='stretch' spacing={4}>
                    <Heading as='h3' size='lg' color='brand.light'>
                      Our Approach
                    </Heading>
                    <List spacing={3}>
                      <ListItem color='gray.300' display='flex' alignItems='center'>
                        <ListIcon as={Brain} color='brand.accent' />
                        <Text>
                          <Text as='span' color='brand.accent' fontWeight='bold'>
                            Neurodiversity-Driven:{' '}
                          </Text>
                          Unique problem-solving approaches
                        </Text>
                      </ListItem>
                      <ListItem color='gray.300' display='flex' alignItems='center'>
                        <ListIcon as={Rocket} color='brand.accent' />
                        <Text>
                          <Text as='span' color='brand.accent' fontWeight='bold'>
                            Innovation Focus:{' '}
                          </Text>
                          Developing adaptive AI solutions
                        </Text>
                      </ListItem>
                      <ListItem color='gray.300' display='flex' alignItems='center'>
                        <ListIcon as={ChevronRight} color='brand.accent' />
                        <Text>
                          <Text as='span' color='brand.accent' fontWeight='bold'>
                            User-Centered:{' '}
                          </Text>
                          Building based on real developer needs
                        </Text>
                      </ListItem>
                    </List>
                  </VStack>
                </Box>
              </GridItem>
            </Grid>

            {/* Investment Opportunity */}
            <Box
              p={{ base: 6, md: 8 }}
              borderRadius='xl'
              bg='rgba(11, 61, 145, 0.3)'
              border='1px solid'
              borderColor='brand.accent'>
              <VStack spacing={6} align='stretch'>
                <Heading as='h3' size='lg' color='brand.light'>
                  Investment Opportunity
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <VStack align='stretch' spacing={4}>
                    <Text color='gray.300'>
                      We're seeking strategic partners who share our vision of making development
                      more inclusive and efficient through AI innovation. Join us in building the
                      future of development tools.
                    </Text>
                    <List spacing={3}>
                      <ListItem color='gray.300' display='flex' alignItems='center'>
                        <ListIcon as={ChevronRight} color='brand.accent' />
                        Focus on Product Development & Research
                      </ListItem>
                      <ListItem color='gray.300' display='flex' alignItems='center'>
                        <ListIcon as={ChevronRight} color='brand.accent' />
                        Building a Strong Technical Team
                      </ListItem>
                      <ListItem color='gray.300' display='flex' alignItems='center'>
                        <ListIcon as={ChevronRight} color='brand.accent' />
                        Expanding Our Development Tools Suite
                      </ListItem>
                    </List>
                  </VStack>
                  <VStack spacing={4}>
                    <Button
                      leftIcon={<Download />}
                      variant='primary'
                      size='lg'
                      width='full'
                      onClick={handlePitchDeckRequest}>
                      Request Pitch Deck
                    </Button>
                    <Button
                      leftIcon={<Calendar />}
                      variant='outline'
                      size='lg'
                      width='full'
                      onClick={handleScheduleCall}>
                      Schedule Investor Call
                    </Button>
                    <Button
                      leftIcon={<Mail />}
                      variant='ghost'
                      size='lg'
                      width='full'
                      onClick={() => {
                        const element = document.getElementById('contact');
                        element?.scrollIntoView({ behavior: 'smooth' });
                      }}>
                      Contact Us
                    </Button>
                  </VStack>
                </SimpleGrid>
              </VStack>
            </Box>

            {/* Disclaimer */}
            <Alert
              status='info'
              variant='subtle'
              bg='rgba(11, 61, 145, 0.3)'
              borderRadius='xl'
              borderWidth='1px'
              borderColor='brand.accent'>
              <AlertIcon />
              <Text fontSize='sm' color='gray.300'>
                This presentation contains forward-looking statements. Actual results may differ
                materially from these projections. Investment involves risk. Please consult with
                your financial advisor before making investment decisions.
              </Text>
            </Alert>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default Investors;
